import { lifecycleEventTypes } from "../config/events.js";
import { getParams, getRule } from "./utils.js";
import Vortex from "./Vortex.js";
import EventBus from "./EventBus.js";
import Container from "./Container.js";
import State from "./State.js";

export function component({ name, container = null, components = [], state = {} }) {
  if (!name) throw new Error("Component names are required.");
  return function decorator(target) {
    return class extends target {
      constructor( ...args ) {
        super( ...args );

        this.name = name;
        this.components = components;
        this.container = new Container(container);
        this._state = new State(state);
      }
    }
  }
}

export function watcher(name) {
  return function(target, _, descriptor) {
    const fn = descriptor.value;
    if (!target.watchers) target.watchers = [];
    target.watchers.push({ name, fn }) 
  }
}

export default class VortexComponent {
  DOMevents = {};

  constructor({ props = {}, parent = null, paths = [] }) {
    this.rendered = false;
    this.path = window.location.pathname;
    this.parent = parent;
    this.eventBus = this.parent ? this.parent.eventBus : new EventBus();
    this.subscribe = this.eventBus.subscribe;
    this.emit = this.eventBus.emit;
    this.paths = paths;
    this.props = props;

    for (const key in Vortex.meta) {
      const meta = Vortex.meta[key];
      this["$"+key] = meta;
    }
  }

  get params() {
    const pathname = this.path;
    const rule = getRule(pathname, this.paths);
    const params = getParams(pathname, rule);

    return {
      ...params
    }
  }

  get state() {
    return this._state.public || {};
  }

  render() {
    if (this.rendered) return;

    // Instantiate components
    if (this.components) {
      this.components = [...new Set(this.components)].map(Component => {
        if (Component instanceof VortexComponent) return Component;
        
        if (typeof Component === "function") {
          return new Component({ 
            parent: this, 
            paths: this.paths
          })
        }
      });
    }

    // Initialize watchers
    this.components.forEach(component => {
      if (component.watchers) component.initWatchers();
    });

    // Setup lifecycle hooks
    if (this.beforeLoad) this.beforeLoad();
    for (const [hook, event] of Object.entries(lifecycleEventTypes)) {
      if (this[hook]) { 
        if (
          hook === "load"
          && (document.readyState === "complete"
          || document.readyState === "interactive")
        ) {
          this[hook]();
        } else {
          window.addEventListener(event, this[hook]);
        }
      }
    }

    // Execute target-specifc logic if present
    if (
      typeof TARGET !== "undefined" 
      && typeof this[TARGET] !== "undefined" 
      && typeof this[TARGET] === "function") 
    {
      this[TARGET]();
    } 

    // Render child components
    for (const component of this.components) {
      this._state.attach(component.name, component);
      if (component.render && !component.rendered) {
        component.render.bind(component)();
      }
    }

    this.rendered = true;
  }

  initWatchers() {
    for (const { name, fn } of this.watchers) {
      if (fn) {
        fn = fn.bind(this);
        this.subscribe(`${name}`, fn);
      }
    }
  } 

  addComponent(Class, opts) {
    const args = {
      ...opts,
      parent: this,
      paths: this.paths,
    }

    const component = new Class(args);

    this.components.push(component);
    component.render();
  }

  getComponent(name) {
    return this.components.find(c => c.name === name);
  }

  addEventListener(element, type, handler) {
    element.addEventListener(type, handler);
  }

  removeEventListener(element, type) {
    element.removeEventListener(type);
  }
}
